export default function RoofTypesElPaso() {
  const roofTypes = [
    {
      name: 'Clay and Concrete Tile',
      popularity: 'Very Common',
      lifespan: '50+ years',
      image: 'https://public.readdy.ai/ai/img_res/385663fe2c00d7978163d92ba7793c56.jpg',
      pros: ['Excellent heat resistance', 'Long-lasting durability', 'Classic southwestern aesthetic', 'Fire resistant', 'Low maintenance'],
      cons: ['Higher initial cost', 'Requires structural support', 'Professional installation needed'],
      description: 'The most iconic roofing choice in El Paso, clay and concrete tiles perfectly suit our climate. They reflect heat, withstand UV exposure, and complement southwestern architecture. Popular in established neighborhoods and new custom homes.'
    },
    {
      name: 'Asphalt Shingles',
      popularity: 'Most Common',
      lifespan: '20-30 years',
      image: 'https://readdy.ai/api/search-image?query=Modern%20residential%20home%20in%20El%20Paso%20with%20quality%20architectural%20asphalt%20shingle%20roof%2C%20dimensional%20shingles%20in%20earth%20tone%20colors%2C%20contemporary%20suburban%20house%20with%20desert%20landscape%2C%20clear%20blue%20sky%2C%20professional%20photography%20showing%20durable%20roofing%20installation%20and%20clean%20lines&width=600&height=400&seq=roof-shingle-elpaso-021&orientation=landscape',
      pros: ['Affordable option', 'Wide color selection', 'Easy to repair', 'Readily available', 'Good wind resistance'],
      cons: ['Shorter lifespan in intense sun', 'Requires quality grade for El Paso', 'Less energy efficient'],
      description: 'Architectural asphalt shingles are the most budget-friendly option and widely used across El Paso. Choose premium grades with high wind ratings and UV protection. Lighter colors perform better in our climate by reflecting heat.'
    },
    {
      name: 'Metal Roofing',
      popularity: 'Increasingly Popular',
      lifespan: '40-70 years',
      image: 'https://readdy.ai/api/search-image?query=Contemporary%20El%20Paso%20home%20with%20modern%20metal%20roof%20installation%2C%20standing%20seam%20metal%20roofing%20in%20neutral%20color%2C%20sleek%20architectural%20design%20with%20mountain%20views%2C%20bright%20desert%20setting%2C%20professional%20photography%20showing%20energy-efficient%20metal%20roofing%20system%20and%20modern%20construction%20quality&width=600&height=400&seq=roof-metal-elpaso-022&orientation=landscape',
      pros: ['Superior energy efficiency', 'Extremely durable', 'Reflects solar heat', 'Fire and wind resistant', 'Environmentally friendly'],
      cons: ['Higher upfront investment', 'Can be noisy in rain', 'Requires experienced installer'],
      description: 'Metal roofing is gaining popularity in El Paso for its exceptional performance in desert conditions. Standing seam and corrugated metal options reflect up to 70% of solar radiation, dramatically reducing cooling costs. Ideal for modern and ranch-style homes.'
    },
    {
      name: 'Flat/Low-Slope Membrane',
      popularity: 'Common on Commercial',
      lifespan: '15-30 years',
      image: 'https://readdy.ai/api/search-image?query=Modern%20southwestern%20style%20home%20in%20El%20Paso%20with%20flat%20roof%20design%2C%20contemporary%20architecture%20with%20low-slope%20membrane%20roofing%20system%2C%20clean%20geometric%20lines%2C%20desert%20landscaping%2C%20mountain%20backdrop%2C%20professional%20architectural%20photography%20showing%20quality%20flat%20roof%20installation%20and%20modern%20design&width=600&height=400&seq=roof-flat-elpaso-023&orientation=landscape',
      pros: ['Suits southwestern architecture', 'Cost-effective for large areas', 'Easy HVAC access', 'Modern aesthetic'],
      cons: ['Requires excellent drainage', 'More maintenance needed', 'Shorter lifespan than pitched roofs'],
      description: 'Flat and low-slope roofs are common in El Paso\'s commercial buildings and modern residential designs. TPO, EPDM, and modified bitumen membranes work well. Critical factors include proper drainage design and quality installation to prevent ponding water in our occasional heavy rains.'
    }
  ];

  return (
    <section className="py-20 px-6 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Common Roof Types in El Paso
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Understanding the roofing materials that perform best in El Paso's desert climate helps you make informed decisions. Each type has unique advantages suited to different architectural styles and budgets.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {roofTypes.map((type, index) => (
            <div key={index} className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={type.image} 
                  alt={`${type.name} roofing in El Paso`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{type.name}</h3>
                    <div className="flex gap-4 text-sm">
                      <span className="text-teal-600 font-medium">{type.popularity}</span>
                      <span className="text-gray-600">Lifespan: {type.lifespan}</span>
                    </div>
                  </div>
                </div>
                
                <p className="text-sm text-gray-700 mb-4 leading-relaxed">{type.description}</p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <i className="ri-check-line text-green-600"></i>
                      Advantages
                    </h4>
                    <ul className="space-y-1">
                      {type.pros.map((pro, i) => (
                        <li key={i} className="text-xs text-gray-600 flex items-start gap-2">
                          <span className="text-green-600 mt-0.5">•</span>
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <i className="ri-information-line text-orange-600"></i>
                      Considerations
                    </h4>
                    <ul className="space-y-1">
                      {type.cons.map((con, i) => (
                        <li key={i} className="text-xs text-gray-600 flex items-start gap-2">
                          <span className="text-orange-600 mt-0.5">•</span>
                          {con}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
